import React, { useState } from "react";
import { api } from "@/lib/api";
import { API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Upload, AlertTriangle, CheckCircle2, FileJson, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export default function MigrationPanel() {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  // Download snapshot — go via fetch so the file streams cleanly.
  const exportSnapshot = async () => {
    try {
      const url = `${API}/admin/data-snapshot`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) {
        toast.error(`Export failed (${r.status})`);
        return;
      }
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `xentra-snapshot-${new Date().toISOString().slice(0, 10)}.json`;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      a.style.display = "none";
      // Anchor must be in the DOM for Firefox/Safari to honour the click() download
      document.body.appendChild(a);
      a.click();
      // Slight delay before cleanup so the browser registers the download
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      }, 250);
      toast.success(`Snapshot downloaded (${(blob.size / 1024).toFixed(0)} KB) — check your Downloads folder`);
    } catch (e) {
      toast.error("Export failed: " + (e?.message || "unknown"));
    }
  };

  // Download QA checklist xlsx
  const downloadQaChecklist = async () => {
    try {
      const url = `${API}/admin/qa-checklist`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) {
        toast.error(`Download failed (${r.status})`);
        return;
      }
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `xentra-qa-checklist-${new Date().toISOString().slice(0, 10)}.xlsx`;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      }, 250);
      toast.success(`QA checklist downloaded (${(blob.size / 1024).toFixed(0)} KB)`);
    } catch (e) {
      toast.error("Download failed: " + (e?.message || "unknown"));
    }
  };

  const importSnapshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(
      `This will WIPE production's business collections and replace them with the snapshot from "${file.name}". Continue?`
    )) {
      e.target.value = "";
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/data-restore?wipe_first=true", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success("Snapshot restored");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="migration-panel">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700 flex items-center gap-1.5">
          <FileJson className="h-3 w-3" /> Data migration
        </div>
        <h3 className="font-heading text-xl font-bold text-slate-900 mt-1">Move data between environments</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl">
          Snapshot all business data from this environment (accounts, deals, leads, budget tracker, tasks, etc.) and restore it onto another. Auth tokens, sessions, Xero credentials and operational logs are excluded.
        </p>
      </div>

      {/* QA checklist */}
      <Card className="border-blue-200">
        <CardHeader className="border-b border-blue-100 py-3 bg-blue-50/40">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-blue-700" /> QA checklist (.xlsx)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-3">
          <p className="text-xs text-slate-600">
            A spreadsheet with tick-boxes for every functional area of the CRM — 124 test cases across 26 sections (Auth, Dashboard, Accounts, Deals, Tickets V2, Support Configuration, Board 60-day Action, integrations, RBAC, mobile, security). Includes a roll-up Summary sheet that counts tested / passed / failed automatically.
          </p>
          <Button
            data-testid="download-qa-checklist"
            onClick={downloadQaChecklist}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700"
          >
            <Download className="h-4 w-4" /> Download QA checklist
          </Button>
        </CardContent>
      </Card>

      {/* Export */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 py-3">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Download className="h-4 w-4 text-emerald-600" /> Export snapshot
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-3">
          <p className="text-xs text-slate-600">
            Downloads a single JSON file containing every record from this environment's business collections. Use this on <b>preview</b> when you want to push the current data to production.
          </p>
          <Button data-testid="export-snapshot" onClick={exportSnapshot} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
            <Download className="h-4 w-4" /> Export snapshot
          </Button>
        </CardContent>
      </Card>

      {/* Import */}
      <Card className="border-amber-200">
        <CardHeader className="border-b border-amber-100 py-3 bg-amber-50/50">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Upload className="h-4 w-4 text-amber-700" /> Import snapshot
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-3">
          <div className="flex gap-2 items-start p-3 border border-amber-200 bg-amber-50 rounded-md text-xs text-amber-900">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <b>Destructive operation.</b> All records in the business collections (accounts, deals, leads, tickets, projects, budget_tracker, etc.) will be replaced with the snapshot's contents. Users, sessions, Xero tokens, email logs and WAF config are not touched.
            </div>
          </div>
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="file"
              accept="application/json,.json"
              onChange={importSnapshot}
              className="hidden"
              data-testid="import-snapshot-input"
              disabled={importing}
            />
            <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium ${
              importing ? "bg-slate-200 text-slate-500" : "bg-amber-600 hover:bg-amber-700 text-white"
            }`}>
              <Upload className="h-4 w-4" /> {importing ? "Importing…" : "Choose file & import"}
            </span>
          </label>
        </CardContent>
      </Card>

      {/* Last result */}
      {result && (
        <Card className="border-emerald-200">
          <CardHeader className="border-b border-emerald-100 py-3 bg-emerald-50/40">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Import complete
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 text-xs space-y-3">
            <div className="text-slate-600">
              Snapshot from <span className="font-mono">{result.snapshot_from}</span> generated {new Date(result.snapshot_generated_at).toLocaleString("en-GB")}.
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="px-2 py-1.5 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">Collection</th>
                  <th className="px-2 py-1.5 text-right font-mono text-[10px] uppercase tracking-wider text-slate-500">Inserted</th>
                  <th className="px-2 py-1.5 text-center font-mono text-[10px] uppercase tracking-wider text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(result.results || {}).map(([col, info]) => (
                  <tr key={col} className="border-b border-slate-100">
                    <td className="px-2 py-1.5 font-mono">{col}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{info.inserted ?? "—"}</td>
                    <td className="px-2 py-1.5 text-center">
                      {info.error
                        ? <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">{info.error}</Badge>
                        : info.skipped
                          ? <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">{info.skipped}</Badge>
                          : <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">OK</Badge>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
